import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from './session';
import {
  fetchGame,
  myTarget,
  confirmKill,
  declineKill,
  type GameEvent,
  type GameDetailResponse,
  type WeaponBriefResponse,
} from './games';
import { API_BASE } from './api';

export type PendingKill = {
  requestId: number;
  killerName: string | null;
  happenedAt: string;
};

export type WitnessReportNotif = {
  reportId: number;
  reporterName: string | null;
  victimName: string | null;
  weaponName: string | null;
};

export type ModPendingReport = {
  reportId: number;
  killerName: string | null;
  victimName: string | null;
  weaponName: string | null;
  killerReason: string;
};

type UseGameEventsResult = {
  game: GameDetailResponse | null;
  myCurrentTarget: string | null;
  myCurrentWeapon: WeaponBriefResponse | null;
  pendingKill: PendingKill | null;
  killAccepted: boolean;
  killDeclined: boolean;
  killCount: number;
  dismissKillAccepted: () => void;
  dismissKillDeclined: () => void;
  acceptPendingKill: () => Promise<void>;
  declinePendingKill: () => Promise<void>;
  refreshTarget: () => Promise<void>;
  refreshGame: () => Promise<void>;
  weaponEventCount: number;
  pendingWitnessReport: WitnessReportNotif | null;
  wasArrested: boolean;
  dismissArrested: () => void;
  pendingModReports: ModPendingReport[];
  liveEvents: GameEvent[];
  killRequestCount: number;
};

export function useGameEvents(session: Session | null): UseGameEventsResult {
  const [game, setGame] = useState<GameDetailResponse | null>(null);
  const [myCurrentTarget, setMyCurrentTarget] = useState<string | null>(null);
  const [myCurrentWeapon, setMyCurrentWeapon] = useState<WeaponBriefResponse | null>(null);
  const [pendingKill, setPendingKill] = useState<PendingKill | null>(null);
  const [killAccepted, setKillAccepted] = useState(false);
  const [killDeclined, setKillDeclined] = useState(false);
  const [killCount, setKillCount] = useState(0);
  const [weaponEventCount, setWeaponEventCount] = useState(0);
  const [pendingWitnessReport, setPendingWitnessReport] = useState<WitnessReportNotif | null>(null);
  const [wasArrested, setWasArrested] = useState(false);
  const [pendingModReports, setPendingModReports] = useState<ModPendingReport[]>([]);
  // keeps full info per report ID so witness_response can correlate it for the moderator panel
  const witnessReportMapRef = useRef<
    Map<number, WitnessReportNotif & { killerName: string | null }>
  >(new Map());
  const [liveEvents, setLiveEvents] = useState<GameEvent[]>([]);
  const [killRequestCount, setKillRequestCount] = useState(0);

  const code = session?.code ?? '';
  const playerName = session?.playerName ?? '';
  const token = session?.token ?? '';

  useEffect(() => {
    if (!code) return;
    fetchGame(code)
      .then(setGame)
      .catch(() => {});
  }, [code]);

  useEffect(() => {
    if (!session) return;
    myTarget(code, playerName, token)
      .then((r) => {
        setMyCurrentTarget(r.target);
        setMyCurrentWeapon(r.weapon);
      })
      .catch(() => {});
  }, [session, code, playerName, token]);

  // SSE subscription — all setState calls happen inside the onmessage callback
  useEffect(() => {
    if (!session) return;

    const url = `${API_BASE}/games/${session.code}/stream/`;
    const source = new EventSource(url);

    source.onmessage = (e: MessageEvent<string>) => {
      const event = JSON.parse(e.data) as GameEvent;
      const me = session.playerName;

      setLiveEvents((prev) => [event, ...prev].slice(0, 100));

      if (event.type === 'kill_request') {
        setKillRequestCount((n) => n + 1);
      }

      if (event.type === 'kill_request' && event.target === me) {
        const rid = event.meta?.request_id;
        if (typeof rid === 'number') {
          setPendingKill({
            requestId: rid,
            killerName: event.actor ?? null,
            happenedAt: event.happened_at,
          });
        }
      }

      if (event.type === 'kill_decline' && event.target === me) {
        setPendingKill(null);
      }

      if (event.type === 'kill' || event.type === 'kill_accept') {
        fetchGame(session.code)
          .then(setGame)
          .catch(() => {});
        if (event.actor === me || event.target === me) {
          myTarget(session.code, session.playerName, session.token)
            .then((r) => {
              setMyCurrentTarget(r.target);
              setMyCurrentWeapon(r.weapon);
            })
            .catch(() => {});
        }
        if (event.type === 'kill_accept' && event.actor === me) {
          setKillAccepted(true);
          setKillCount((n) => n + 1);
        }
      }

      if (event.type === 'kill_decline' && event.actor === me) {
        setKillDeclined(true);
      }

      if (event.type === 'join' || event.type === 'leave' || event.type === 'kick') {
        const name = event.type === 'kick' ? event.target : event.actor;
        if (name) {
          setGame((prev) => {
            if (!prev) return prev;
            if (event.type === 'join') {
              const exists = prev.members.some((m) => m.name === name);
              if (exists) {
                return {
                  ...prev,
                  members: prev.members.map((m) =>
                    m.name === name ? { ...m, status: 'active' } : m
                  ),
                };
              }
              return { ...prev, members: [...prev.members, { name, status: 'active' }] };
            }
            return {
              ...prev,
              members: prev.members.filter((m) => m.name !== name),
            };
          });
        }
      }

      if (event.type === 'restart') {
        fetchGame(session.code)
          .then(setGame)
          .catch(() => {});
        setMyCurrentTarget(null);
        setMyCurrentWeapon(null);
        setKillCount(0);
        setPendingKill(null);
        setKillAccepted(false);
        setKillDeclined(false);
        setPendingWitnessReport(null);
        setWasArrested(false);
        setPendingModReports([]);
        witnessReportMapRef.current.clear();
        setLiveEvents([]);
        setKillRequestCount(0);
      }

      if (event.type === 'start' || event.type === 'end') {
        fetchGame(session.code)
          .then(setGame)
          .catch(() => {});
        if (event.type === 'start') {
          myTarget(session.code, session.playerName, session.token)
            .then((r) => {
              setMyCurrentTarget(r.target);
              setMyCurrentWeapon(r.weapon);
            })
            .catch(() => {});
        }
      }

      if (event.type === 'witness_report') {
        const rid = event.meta?.report_id as number;
        const info = {
          reportId: rid,
          reporterName: event.actor ?? null,
          killerName: event.target ?? null,
          victimName: (event.meta?.victim as string) ?? null,
          weaponName: (event.meta?.weapon as string) ?? null,
        };
        witnessReportMapRef.current.set(rid, info);
        if (event.target === me) {
          setPendingWitnessReport({
            reportId: rid,
            reporterName: info.reporterName,
            victimName: info.victimName,
            weaponName: info.weaponName,
          });
        }
      }

      if (event.type === 'witness_response') {
        const rid = event.meta?.report_id as number;
        const accepted = event.meta?.accepted as boolean;
        const reason = (event.meta?.reason as string) ?? '';
        if (event.actor === me) {
          setPendingWitnessReport(null);
        }
        if (!accepted) {
          const stored = witnessReportMapRef.current.get(rid);
          if (stored) {
            setPendingModReports((prev) => [
              ...prev,
              {
                reportId: rid,
                killerName: stored.killerName,
                victimName: stored.victimName,
                weaponName: stored.weaponName,
                killerReason: reason,
              },
            ]);
          }
        }
      }

      if (event.type === 'mod_ruling') {
        const rid = event.meta?.report_id as number;
        setPendingModReports((prev) => prev.filter((r) => r.reportId !== rid));
      }

      if (event.type === 'arrest') {
        if (event.target === me) {
          setWasArrested(true);
        }
        fetchGame(session.code)
          .then(setGame)
          .catch(() => {});
      }

      if (
        event.type === 'weapon_suggest' ||
        event.type === 'weapon_approve' ||
        event.type === 'weapon_decline'
      ) {
        // weaponEventCount lets PlayPage re-fetch the weapon pool for the moderator.
        setWeaponEventCount((n) => n + 1);
      }
    };

    return () => source.close();
  }, [session]);

  const refreshGame = useCallback(async () => {
    if (!code) return;
    try {
      const g = await fetchGame(code);
      setGame(g);
    } catch {
      // ignore
    }
  }, [code]);

  const refreshTarget = useCallback(async () => {
    if (!session) return;
    try {
      const r = await myTarget(code, playerName, token);
      setMyCurrentTarget(r.target);
      setMyCurrentWeapon(r.weapon);
    } catch {
      // target unavailable
    }
  }, [session, code, playerName, token]);

  const acceptPendingKill = useCallback(async () => {
    if (!session || !pendingKill) return;
    try {
      await confirmKill(code, playerName, token, pendingKill.requestId);
    } catch {
      // kill request already resolved
    }
    setPendingKill(null);
    await refreshTarget();
  }, [session, pendingKill, code, playerName, token, refreshTarget]);

  const declinePendingKill = useCallback(async () => {
    if (!session || !pendingKill) return;
    try {
      await declineKill(code, playerName, token, pendingKill.requestId);
    } catch {
      // already resolved
    }
    setPendingKill(null);
  }, [session, pendingKill, code, playerName, token]);

  const dismissKillAccepted = useCallback(() => setKillAccepted(false), []);
  const dismissKillDeclined = useCallback(() => setKillDeclined(false), []);
  const dismissArrested = useCallback(() => setWasArrested(false), []);

  return {
    game,
    myCurrentTarget,
    myCurrentWeapon,
    pendingKill,
    killAccepted,
    killDeclined,
    killCount,
    dismissKillAccepted,
    dismissKillDeclined,
    acceptPendingKill,
    declinePendingKill,
    refreshTarget,
    refreshGame,
    weaponEventCount,
    pendingWitnessReport,
    wasArrested,
    dismissArrested,
    pendingModReports,
    liveEvents,
    killRequestCount,
  };
}
